import type { AccountService } from './index';
import { signal, computed } from '@preact/signals-react';
import type { QueryClient } from '@tanstack/query-core';
import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import { getAccountsByPermissionQuery } from 'src/queries/accounts';
import { log } from 'src/services/AccountService/log';

/**
 * Service responsible for managing account loading and validation
 */
export class AccountServiceImpl implements AccountService {
    // Account validation signals
    // eslint-disable-next-line no-restricted-syntax -- required pattern
    private readonly availableAccounts = signal<ConcreteAccount[]>([]);
    // eslint-disable-next-line no-restricted-syntax -- required pattern
    private readonly isLoadingAccounts = signal<boolean>(false);
    // eslint-disable-next-line no-restricted-syntax -- required pattern
    private readonly accountLoadingFailed = signal<boolean>(false);

    // Computed signal for account name to ID mapping
    // eslint-disable-next-line no-restricted-syntax -- required pattern
    private readonly accountNameToIdMap = computed<Map<string, string>>(() => {
        const map = new Map<string, string>();

        try {
            const accounts = this.availableAccounts.value;
            if (Array.isArray(accounts)) {
                for (const account of accounts) {
                    if (account && typeof account === 'object' && account.name && account.id) {
                        // Map both exact name and trimmed lowercase for case-insensitive matching
                        map.set(account.name, account.id);
                        map.set(account.name.toLowerCase().trim(), account.id);
                    }
                }
            }
        } catch (_error) {
            // Handle any errors in mapping calculation
        }

        return map;
    });

    constructor(private readonly queryClient: QueryClient) {}

    /**
     * Load available accounts
     */
    async loadAccounts(permission: PermissionType = 'reader'): Promise<void> {
        try {
            this.isLoadingAccounts.value = true;
            this.accountLoadingFailed.value = false;

            const query = getAccountsByPermissionQuery(permission);
            const accounts = await this.queryClient.fetchQuery(query);

            this.availableAccounts.value = accounts || [];

            log.debug('Loaded accounts', {
                accountCount: accounts?.length || 0,
            });
        } catch (error) {
            log.error({
                message: 'Failed to load accounts',
                error: error instanceof Error ? error : new Error(String(error)),
            });
            this.availableAccounts.value = [];
            this.accountLoadingFailed.value = true;
        } finally {
            this.isLoadingAccounts.value = false;
        }
    }

    /**
     * Check if an account name is valid (exists in user's available accounts)
     */
    hasValidAccount(accountName: string): boolean {
        // Defensive checks
        if (!accountName || typeof accountName !== 'string') {
            return true; // Empty is not an error
        }

        // If accounts are still loading, treat all accounts as valid to avoid false negatives
        if (this.isLoadingAccounts.value) {
            return true;
        }

        // If account loading failed, treat all accounts as valid to avoid false negatives
        if (this.accountLoadingFailed.value) {
            return true;
        }

        // If no accounts have been loaded yet, treat all accounts as valid to avoid false negatives
        if (this.availableAccounts.value.length === 0) {
            return true;
        }

        const accountMap = this.accountNameToIdMap.value;
        if (!accountMap || typeof accountMap.get !== 'function') {
            return true; // If validation data not ready, don't flag as error
        }

        return accountMap.has(accountName);
    }

    /**
     * Get the account UUID for a given account name
     */
    getAccountId(accountName: string): string | null {
        if (!accountName || typeof accountName !== 'string' || !accountName.trim()) {
            return null;
        }

        const accountMap = this.accountNameToIdMap.value;
        if (!accountMap || typeof accountMap.get !== 'function') {
            return null;
        }

        const trimmedName = accountName.trim();

        // Try exact match first, then case-insensitive
        return accountMap.get(trimmedName) || accountMap.get(trimmedName.toLowerCase()) || null;
    }

    /**
     * Readonly access to account loading state
     */
    get isLoading(): boolean {
        return this.isLoadingAccounts.value;
    }

    /**
     * Readonly access to account loading failure state
     */
    get loadingFailed(): boolean {
        return this.accountLoadingFailed.value;
    }

    /**
     * Readonly access to available accounts
     */
    get accounts(): ConcreteAccount[] {
        return this.availableAccounts.value;
    }
}
